
import { useState, useEffect, useCallback, useRef } from 'react';
import { Attendee } from '@/types/attendee';
import { useAttendeeCore } from './useAttendeeCore';

/**
 * Main hook for attendee data management with silent refresh mechanism
 */
export const useAttendeesData = (pollingInterval = 0) => { // Set to 0 to disable automatic polling
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [lastRefreshTime, setLastRefreshTime] = useState<string>('');
  
  // Connection state tracking
  const [usingSupabase, setUsingSupabase] = useState<boolean>(true);
  
  // Track if initial data was loaded
  const initialDataLoaded = useRef(false);
  const isRefreshing = useRef(false);
  const pendingUpdates = useRef<Record<string, Attendee>>({});
  
  // Core attendee functionality (Get, Add, Update)
  const { 
    loadAttendees, 
    updateAttendee, 
    setAttendees: coreSetAttendees 
  } = useAttendeeCore();
  
  // Demo data generation - now just a placeholder
  const { generateDemoData, resetData } = { 
    generateDemoData: () => [], 
    resetData: async () => true 
  };
  
  // Process pending updates and apply them to state
  const applyPendingUpdates = useCallback(() => {
    const updates = pendingUpdates.current;
    const codes = Object.keys(updates);
    
    if (codes.length === 0) return;
    
    console.log(`Applying ${codes.length} pending updates silently - NO PAGE REFRESH`);
    
    setAttendees(prev => {
      const updated = [...prev];
      
      // Apply each pending update
      for (const code of codes) {
        const index = updated.findIndex(a => a.code === code);
        if (index >= 0) {
          updated[index] = updates[code];
        }
      }
      
      return updated;
    });
    
    // Clear pending updates
    pendingUpdates.current = {};
    
    // Update refresh time without full refresh
    const newTime = new Date().toLocaleTimeString('vi-VN');
    setLastRefreshTime(newTime);
  }, []);
  
  // Initial data load
  useEffect(() => {
    const loadInitialData = async () => {
      if (initialDataLoaded.current) return;
      
      setIsLoading(true);
      try {
        const loadedAttendees = await loadAttendees();
        setAttendees(loadedAttendees);
        initialDataLoaded.current = true;
        setLastRefreshTime(new Date().toLocaleTimeString('vi-VN'));
      } catch (error) {
        // Error handling without console.log
      } finally {
        setIsLoading(false);
      }
    };
    
    loadInitialData();
  }, [loadAttendees]);
  
  // Apply any pending updates every 300ms to batch UI updates
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (Object.keys(pendingUpdates.current).length > 0) {
        applyPendingUpdates();
      }
    }, 300);
    
    return () => clearInterval(intervalId);
  }, [applyPendingUpdates]);
  
  // Method to refresh attendees data with forced cache bypass and silent update
  const refreshAttendees = useCallback(async () => {
    if (isRefreshing.current) {
      console.log('Silent refresh already in progress - NO PAGE REFRESH');
      return attendees;
    }
    
    isRefreshing.current = true;
    console.log('Starting silent data refresh - NO PAGE REFRESH');
    
    // Don't set isLoading to true for silent refreshes
    try {
      // Force reload from database
      const loadedAttendees = await loadAttendees();
      
      // Only update state if there are actual changes
      if (JSON.stringify(loadedAttendees) !== JSON.stringify(attendees)) {
        console.log('Data changed, applying silent update - NO PAGE REFRESH');
        setAttendees(loadedAttendees);
        
        // Update the refresh timestamp
        const newTime = new Date().toLocaleTimeString('vi-VN');
        setLastRefreshTime(newTime);
        
        // Dispatch a custom event for UI components to react to data changes
        window.dispatchEvent(new CustomEvent('data-sync-completed', {
          detail: { timestamp: newTime, changedData: true }
        }));
      } else {
        // Still update refresh time for UI feedback
        console.log('No data changed, only updating timestamp - NO PAGE REFRESH');
        const newTime = new Date().toLocaleTimeString('vi-VN');
        setLastRefreshTime(newTime);
      }
      
      isRefreshing.current = false;
      return loadedAttendees;
    } catch (error) {
      console.error('Error during silent refresh:', error);
      isRefreshing.current = false;
      return attendees;
    }
  }, [loadAttendees, attendees]);
  
  // Listen only for explicit refresh events
  useEffect(() => {
    const handleDataUpdate = (event: CustomEvent) => {
      if (isRefreshing.current) return;
      
      const attendeeCode = event.detail?.attendeeCode;
      const updatedAttendee = event.detail?.attendee || event.detail?.updatedAttendee;
      
      // If we have a specific attendee update, handle it optimistically
      if (attendeeCode && updatedAttendee) {
        console.log(`Silently updating attendee ${attendeeCode} - NO PAGE REFRESH`);
        // Queue update rather than immediately applying
        pendingUpdates.current[attendeeCode] = updatedAttendee;
      }
      // Otherwise do a full refresh if explicitly requested
      else if (event.detail?.forceRefresh) {
        console.log('Force refresh requested, but using silent refresh - NO PAGE REFRESH');
        refreshAttendees();
      }
    };
    
    const handleDataSyncCompleted = (event: CustomEvent) => {
      // Update refresh time without necessarily refreshing data again
      if (event.detail?.timestamp) {
        setLastRefreshTime(event.detail.timestamp);
      }
    };
    
    window.addEventListener('attendance-confirmed', handleDataUpdate as EventListener);
    window.addEventListener('attendance-ui-update', handleDataUpdate as EventListener);
    window.addEventListener('database-update-success', handleDataUpdate as EventListener);
    window.addEventListener('data-sync-completed', handleDataSyncCompleted as EventListener);
    
    return () => {
      window.removeEventListener('attendance-confirmed', handleDataUpdate as EventListener);
      window.removeEventListener('attendance-ui-update', handleDataUpdate as EventListener);
      window.removeEventListener('database-update-success', handleDataUpdate as EventListener);  
      window.removeEventListener('data-sync-completed', handleDataSyncCompleted as EventListener);
    };
  }, [refreshAttendees]);
  
  // Silently update a specific attendee in the local state
  const silentlyUpdateAttendee = useCallback((updatedAttendee: Attendee) => {
    if (!updatedAttendee || !updatedAttendee.code) return;
    
    console.log(`Silently updating attendee ${updatedAttendee.code} - NO PAGE REFRESH`);
    
    // Add to pending updates for batched processing
    pendingUpdates.current[updatedAttendee.code] = updatedAttendee;
    
    // Notify without full refresh
    const newTime = new Date().toLocaleTimeString('vi-VN');
    window.dispatchEvent(new CustomEvent('data-sync-completed', {
      detail: { 
        timestamp: newTime, 
        attendeeCode: updatedAttendee.code,
        attendee: updatedAttendee 
      }
    }));
  }, []);
  
  // Memoize core functionality for stability
  return {
    attendees,
    isLoading,
    refreshAttendees,
    silentlyUpdateAttendee,
    lastRefreshTime,
    generateDemoData,
    resetData,
    updateAttendee,
    usingSupabase
  };
};
